/**
 * ScopeRail — the left navigation rail.
 *
 * Sprint 23.7 rework (hybrid Linear/Notion): Home → workspace Surfaces
 * (Workforce / Tables / Library / Connections) → Utility (My tasks / Activity)
 * → a thin divider → the flat top-level agent tree (full-depth, lazy-expanded)
 * → Settings pinned to the bottom. The nav rows are `<RailItem>`s; the tree is
 * a tighter `<RailTreeItem>`. Visual target: `left_rail_rework_mockup_v1`.
 *
 * The top nav block and Settings are fixed; only the agent tree scrolls.
 */

import { useState, useMemo, useEffect, type CSSProperties } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import {
  useListTree,
  useWorkspaceInfo,
  useEntityTypes,
} from "../../../lib/hooks/use-kuvio";
import { useUnifiedConnections } from "../../../hooks/use-connections";
import { useListMyTasks } from "../../hooks/use-list-my-tasks";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useAuth } from "../../../lib/hooks/use-auth";
import { NodeAvatar } from "../primitives";
import { agentPersona } from "../../lib/agent-persona";
import type { LibraryNode } from "../../../lib/types";
import { RailItem } from "./RailItem";
import { RailSection } from "./RailSection";

// ---------------------------------------------------------------------------
// Tree helpers (carried over from the pre-23.7 rail — proven shape logic)
// ---------------------------------------------------------------------------

interface VisibleEntry {
  node: LibraryNode;
  depth: number;
  childCount: number;
}

/** Group agents by parent, sorted, excluding the workspace root node itself. */
function buildChildMap(agents: LibraryNode[], rootId: string | undefined): Map<string, LibraryNode[]> {
  const map = new Map<string, LibraryNode[]>();
  for (const a of agents) {
    if (a.id === rootId) continue;
    const pid = a.parent_id ?? "__orphan__";
    if (!map.has(pid)) map.set(pid, []);
    map.get(pid)!.push(a);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }
  return map;
}

/** Top-level agents: children of the root node, or (fallback) agents whose
 *  parent isn't another agent in the set. */
function topLevelAgents(
  agents: LibraryNode[],
  childMap: Map<string, LibraryNode[]>,
  rootId: string | undefined,
): LibraryNode[] {
  if (rootId && childMap.has(rootId)) return childMap.get(rootId)!;
  const ids = new Set(agents.map((a) => a.id));
  return agents
    .filter((a) => a.id !== rootId && (!a.parent_id || !ids.has(a.parent_id)))
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

/** Index nodes by parent id (one pass) so descendant walks are cheap. */
function buildChildrenByParent(nodes: LibraryNode[]): Map<string, LibraryNode[]> {
  const m = new Map<string, LibraryNode[]>();
  for (const n of nodes) {
    const pid = n.parent_id ?? "__root__";
    if (!m.has(pid)) m.set(pid, []);
    m.get(pid)!.push(n);
  }
  return m;
}

/** Does this folder's subtree contain at least one agent? Pure-content folders
 *  drop out of the rail (reachable via /library instead). */
function folderHasAgentDescendants(folderId: string, childrenByParent: Map<string, LibraryNode[]>): boolean {
  const stack = [...(childrenByParent.get(folderId) ?? [])];
  let guard = 0;
  while (stack.length && guard++ < 10_000) {
    const n = stack.pop()!;
    if (n.type === "agent") return true;
    const kids = childrenByParent.get(n.id);
    if (kids) stack.push(...kids);
  }
  return false;
}

function loadExpanded(key: string | null): Set<string> {
  if (!key) return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* ignore */ }
  return new Set();
}

function saveExpanded(key: string, set: Set<string>): void {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// <RailTreeItem> — one agent/folder row in the flat tree. Tighter than the nav
// <RailItem>s: chevron + small ramp avatar + 12px label + child count.
// ---------------------------------------------------------------------------

interface RailTreeItemProps {
  node: LibraryNode;
  depth: number;
  childCount: number;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}

function RailTreeItem({ node, depth, childCount, isActive, isExpanded, onToggle, onNavigate }: RailTreeItemProps) {
  const [hover, setHover] = useState(false);
  const lifted = isActive || hover;
  const persona = node.type === "folder" ? "gray" : agentPersona(node);
  const hasChildren = childCount > 0;

  return (
    <div
      onClick={onNavigate}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onNavigate(); }}
      title={node.name}
      aria-current={isActive ? "page" : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 6px",
        margin: "0 8px",
        marginLeft: 8 + depth * 14,
        borderRadius: "var(--border-radius-md)",
        cursor: "pointer",
        background: lifted ? "var(--color-background-secondary)" : "transparent",
        transition: "background 120ms ease",
        minWidth: 0,
      }}
    >
      {hasChildren ? (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
          aria-expanded={isExpanded}
          style={chevronBtn}
        >
          <i className={`ti ti-chevron-${isExpanded ? "down" : "right"}`} style={{ fontSize: 11, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
        </button>
      ) : (
        <span style={{ width: 11, flex: "0 0 auto" }} aria-hidden="true" />
      )}

      <NodeAvatar type={node.type} persona={node.type === "agent" ? persona : undefined} size="xs" name={node.name} />

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: lifted ? "var(--color-text-primary)" : "var(--color-text-secondary)",
          fontWeight: isActive ? 500 : 400,
        }}
      >
        {node.name}
      </span>

      {hasChildren && (
        <span style={{ flex: "0 0 auto", fontSize: 10, color: "var(--color-text-tertiary)", fontVariantNumeric: "tabular-nums" }}>
          {childCount}
        </span>
      )}
    </div>
  );
}

const chevronBtn: CSSProperties = {
  flex: "0 0 auto",
  width: 14,
  height: 18,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 0,
};

// ---------------------------------------------------------------------------
// ScopeRail
// ---------------------------------------------------------------------------

export function ScopeRail() {
  const { nodeId } = useParams<{ nodeId: string }>();
  const { currentWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const labels = useThemedLabels();

  const tree = useListTree(currentWorkspaceId ?? undefined);
  const workspace = useWorkspaceInfo();
  const entityTypes = useEntityTypes();
  const connections = useUnifiedConnections();
  const myTasks = useListMyTasks();

  const allNodes = tree.data ?? [];
  const rootNodeId = workspace.data?.root_node_id as string | undefined;

  // Agents (+ folders whose subtree holds an agent) make up the rail tree.
  const agents = useMemo(() => {
    const childrenByParent = buildChildrenByParent(allNodes);
    return allNodes.filter(
      (n) =>
        n.type !== "row" &&
        (n.type === "agent" || (n.type === "folder" && folderHasAgentDescendants(n.id, childrenByParent))),
    );
  }, [allNodes]);
  const childMap = useMemo(() => buildChildMap(agents, rootNodeId), [agents, rootNodeId]);
  const agentById = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  // Expansion state, persisted per workspace. Default: top-level only.
  const storageKey = currentWorkspaceId ? `kuvio_rail_expanded_${currentWorkspaceId}` : null;
  const [expanded, setExpanded] = useState<Set<string>>(() => loadExpanded(storageKey));

  useEffect(() => {
    setExpanded(loadExpanded(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (storageKey) saveExpanded(storageKey, expanded);
  }, [storageKey, expanded]);

  // Auto-expand the ancestor path of the active agent so it's always visible.
  useEffect(() => {
    if (!nodeId || !agentById.has(nodeId)) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let cur = agentById.get(nodeId);
      cur = cur?.parent_id ? agentById.get(cur.parent_id) : undefined;
      let guard = 0;
      while (cur && cur.id !== rootNodeId && guard++ < 50) {
        next.add(cur.id);
        cur = cur.parent_id ? agentById.get(cur.parent_id) : undefined;
      }
      return next.size === prev.size ? prev : next;
    });
  }, [nodeId, agentById, rootNodeId]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Visible (collapse-aware) flat list.
  const visible = useMemo(() => {
    const result: VisibleEntry[] = [];
    const walk = (siblings: LibraryNode[], depth: number) => {
      for (const child of siblings) {
        const kids = childMap.get(child.id) ?? [];
        result.push({ node: child, depth, childCount: kids.length });
        if (kids.length && expanded.has(child.id)) walk(kids, depth + 1);
      }
    };
    walk(topLevelAgents(agents, childMap, rootNodeId), 0);
    return result;
  }, [agents, childMap, rootNodeId, expanded]);

  // Counts.
  const agentCount = useMemo(
    () => agents.filter((a) => a.type === "agent" && a.id !== rootNodeId).length,
    [agents, rootNodeId],
  );
  const tablesCount = entityTypes.data?.length ?? 0;
  // Sprint 24 — `/connections` is the unified surface (MCP + Wrapped). The rail
  // count must match the dashboard's "All" tab, so count both kinds, not just
  // wrapped APIs (the pre-unification source under-counted MCP registrations).
  const connectionsCount = connections.data?.length ?? 0;
  const tasksCount = (myTasks.data?.tasks ?? []).filter(
    (t) => t.status === "pending" || t.status === "in_progress",
  ).length;

  // Active-state detection. In 23.7 Home routes to /workforce but the Workforce
  // surface owns the active highlight there; Home lights up on /home (23.8).
  const path = location.pathname;
  const isActive = {
    home: path.startsWith("/home"),
    workforce: path.startsWith("/workforce"),
    tables: path.startsWith("/tables"),
    workflows: path.startsWith("/workflows"),
    library: path.startsWith("/library"),
    connections: path.startsWith("/connections"),
    tasks: path.startsWith("/my-tasks"),
    activity: path.startsWith("/activity"),
    settings: path.startsWith("/settings"),
  };

  const hasTree = visible.length > 0;

  return (
    <nav style={railStyle} aria-label="Navigation">
      <div style={{ flex: "0 0 auto" }}>
        <RailItem icon="home" label="Home" active={isActive.home} onClick={() => navigate("/home")} />

        <RailSection spacingTop={12}>
          <RailItem icon="users" label={labels.collective} count={agentCount} active={isActive.workforce} onClick={() => navigate("/workforce")} />
          <RailItem icon="table" label="Tables" count={tablesCount} active={isActive.tables} onClick={() => navigate("/tables")} />
          <RailItem icon="git-branch" label="Workflows" active={isActive.workflows} onClick={() => navigate("/workflows")} />
          <RailItem icon="folder" label="Library" active={isActive.library} onClick={() => navigate("/library")} />
          <RailItem icon="plug" label="Connections" count={connectionsCount} active={isActive.connections} onClick={() => navigate("/connections")} />
        </RailSection>

        <RailSection spacingTop={14}>
          <RailItem icon="checkbox" label="My tasks" count={tasksCount} countVariant="info-pill" active={isActive.tasks} onClick={() => navigate("/my-tasks")} />
          <RailItem icon="activity" label="Activity" active={isActive.activity} onClick={() => navigate("/activity")} />
        </RailSection>
      </div>

      {/* Agent tree — the only scrolling region. */}
      <div style={treeScroll}>
        {hasTree && <div style={dividerStyle} />}
        {visible.map(({ node, depth, childCount }) => (
          <RailTreeItem
            key={node.id}
            node={node}
            depth={depth}
            childCount={childCount}
            isActive={node.id === nodeId}
            isExpanded={expanded.has(node.id)}
            onToggle={() => toggle(node.id)}
            onNavigate={() => navigate(`/agents/${node.id}`)}
          />
        ))}
      </div>

      {/* Settings pinned to the bottom. */}
      <div style={settingsBlock}>
        <RailItem icon="settings" label="Settings" iconSize={15} active={isActive.settings} onClick={() => navigate("/settings/workspace")} />
      </div>
    </nav>
  );
}

const railStyle: CSSProperties = {
  width: 240,
  height: "100%",
  borderRight: "0.5px solid var(--color-border-tertiary)",
  background: "var(--color-background-primary)",
  padding: "10px 0",
  display: "flex",
  flexDirection: "column",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  overflow: "hidden",
};

const treeScroll: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const dividerStyle: CSSProperties = {
  margin: "14px 14px 6px",
  height: "0.5px",
  background: "var(--color-border-tertiary)",
  flex: "0 0 auto",
};

const settingsBlock: CSSProperties = {
  flex: "0 0 auto",
  borderTop: "0.5px solid var(--color-border-tertiary)",
  padding: "8px 0 2px",
  marginTop: 8,
};
